"use client";

import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

export default function SectionSubjectMapping() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [subjectTypeById, setSubjectTypeById] = useState({});

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch classes and subjects on mount
  useEffect(() => {
    const fetchInit = async () => {
      try {
        const [classRes, subjectRes] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.classes),
          axiosInstance.get(API_ENDPOINTS.subjects),
        ]);
        setClasses(classRes.data.data?.classes ?? []);
        setSubjects(subjectRes.data.data?.subjects ?? []);
      } catch (err) {
        setError("Failed to load data: " + (err.response?.data?.message ?? err.message));
      } finally {
        setLoadingInit(false);
      }
    };
    fetchInit();
  }, []);

  // Fetch existing assignments when section changes
  useEffect(() => {
    if (!selectedSectionId || !selectedClassId) {
      setSelectedSubjectIds([]);
      setSubjectTypeById({});
      return;
    }
    const fetchAssignments = async () => {
      setLoadingAssignments(true);
      try {
        const res = await axiosInstance.get(
          `${API_ENDPOINTS.sectionSubjects}?sectionId=${selectedSectionId}&classId=${selectedClassId}`
        );
        const assignments = res.data?.data?.sectionSubjects ?? [];
        const ids = assignments.map((a) => a.subject._id);
        const typeMap = {};
        assignments.forEach((a) => {
          typeMap[a.subject._id] = a.subjectType || "elective";
        });
        setSelectedSubjectIds(ids);
        setSubjectTypeById(typeMap);
      } catch (err) {
        setError("Failed to load assignments: " + (err.response?.data?.message ?? err.message));
      } finally {
        setLoadingAssignments(false);
      }
    };
    fetchAssignments();
  }, [selectedSectionId, selectedClassId]);

  // Fetch sections when class changes
  useEffect(() => {
    if (!selectedClassId) {
      setSections([]);
      setSelectedSectionId("");
      return;
    }
    const fetchSections = async () => {
      setLoadingSections(true);
      setSelectedSectionId("");
      setSections([]);
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.sectionsByClass(selectedClassId));
        const data = res.data?.data?.sections ?? res.data?.data ?? [];
        setSections(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load sections: " + (err.response?.data?.message ?? err.message));
      } finally {
        setLoadingSections(false);
      }
    };
    fetchSections();
  }, [selectedClassId]);

  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjectIds((prev) =>
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId]
    );
    setSubjectTypeById((prev) => ({
      ...prev,
      [subjectId]: prev[subjectId] || "elective",
    }));
  };

  const handleSubjectTypeChange = (subjectId, subjectType) => {
    setSubjectTypeById((prev) => ({
      ...prev,
      [subjectId]: subjectType,
    }));
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (!selectedClassId || !selectedSectionId) {
      setError("Please select a class and section.");
      return;
    }
    if (selectedSubjectIds.length === 0) {
      setError("Please select at least one subject.");
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.post(API_ENDPOINTS.sectionSubjects, {
        classId: selectedClassId,
        sectionId: selectedSectionId,
        subjectAssignments: selectedSubjectIds.map((subjectId) => ({
          subjectId,
          subjectType: subjectTypeById[subjectId] || "elective",
        })),
      });
      setSuccess("Subjects assigned successfully.");
      // Re-fetch to reflect saved state (handles cases where backend normalised types)
      const res = await axiosInstance.get(
        `${API_ENDPOINTS.sectionSubjects}?sectionId=${selectedSectionId}&classId=${selectedClassId}`
      );
      const assignments = res.data?.data?.sectionSubjects ?? [];
      setSelectedSubjectIds(assignments.map((a) => a.subject._id));
      const typeMap = {};
      assignments.forEach((a) => { typeMap[a.subject._id] = a.subjectType || "elective"; });
      setSubjectTypeById(typeMap);
    } catch (err) {
      setError("Failed to save: " + (err.response?.data?.message ?? err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loadingInit) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Assign Subjects to Section</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Class */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Class</label>
          <Select
            value={selectedClassId}
            onValueChange={(val) => {
              setSelectedClassId(val);
              setError("");
              setSuccess("");
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select class" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((cls) => (
                <SelectItem key={cls._id} value={cls._id}>
                  {cls.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Section */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Section</label>
          {loadingSections ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Loading sections...</span>
            </div>
          ) : (
            <Select
              value={selectedSectionId}
              onValueChange={(val) => {
                setSelectedSectionId(val);
                setError("");
                setSuccess("");
              }}
              disabled={!selectedClassId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                {sections.map((sec) => (
                  <SelectItem key={sec._id} value={sec._id}>
                    {sec.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Subjects */}
        {selectedSectionId && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Subjects</label>
            {loadingAssignments ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading assignments...</span>
              </div>
            ) : subjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No subjects found.</p>
            ) : (
              <div className="rounded-md border divide-y">
                {subjects.map((subject) => (
                  <label
                    key={subject._id}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedSubjectIds.includes(subject._id)}
                      onCheckedChange={() => handleSubjectToggle(subject._id)}
                    />
                    <div className="flex-1 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{subject.name}</p>
                        <p className="text-xs text-muted-foreground">{subject.code}</p>
                      </div>
                      <Select
                        value={subjectTypeById[subject._id] || "elective"}
                        onValueChange={(value) => handleSubjectTypeChange(subject._id, value)}
                        disabled={!selectedSubjectIds.includes(subject._id)}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Core option removed */}
                          <SelectItem value="elective">Elective</SelectItem>
                          <SelectItem value="optional">Optional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving || !selectedClassId || !selectedSectionId}
          className="w-full"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {saving ? "Saving..." : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}
